import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Inbox, Search } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";
import Input, { Select } from "../../components/system/Input";
import EmptyState from "../../components/system/EmptyState";
import StatusChip from "../../components/system/StatusChip";
import {
  OperatorTable,
  OperatorTableRow,
} from "../../components/system/OperatorTable";

/**
 * OperatorProductRequestsPage — `/platform/companies/:cid/product-requests`
 *
 *   ?status=pending|approved|rejected|all (default pending)
 *   ?store=<storeId>
 *   ?department=<deptId>
 *   ?q=<freetext>
 *
 * Phase 1: READ-ONLY review table. Decisions still live in the tenant
 * admin UI; the operator surface lists for visibility.
 */
const STATUS_TONE = {
  pending: "soon",
  approved: "fresh",
  rejected: "expired",
};

export default function OperatorProductRequestsPage() {
  const { cid } = useParams();
  const [params, setParams] = useSearchParams();
  const [companyName, setCompanyName] = useState("");
  const [requests, setRequests] = useState([]);
  const [stores, setStores] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  const status = params.get("status") || "pending";
  const storeId = params.get("store") || "";
  const deptId = params.get("department") || "";
  const q = params.get("q") || "";

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const load = async () => {
    setLoading(true);
    try {
      const parts = ["limit=500"];
      if (status && status !== "all")
        parts.push(`status=${encodeURIComponent(status)}`);
      if (storeId) parts.push(`store_id=${encodeURIComponent(storeId)}`);
      if (deptId)
        parts.push(`department_id=${encodeURIComponent(deptId)}`);
      const qs = parts.join("&");
      const [det, pr, st, dp] = await Promise.all([
        api.get(`/platform/companies/${cid}`),
        api.get(`/platform/companies/${cid}/product-requests?${qs}`),
        api.get(`/platform/companies/${cid}/stores`),
        api.get(`/platform/companies/${cid}/departments`),
      ]);
      setCompanyName(det.data.company?.name || "");
      setRequests(pr.data.requests || []);
      setStores(st.data.stores || []);
      setDepartments(dp.data.departments || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [cid, status, storeId, deptId]); // eslint-disable-line react-hooks/exhaustive-deps

  const storeName = useMemo(() => {
    const map = new Map(stores.map((s) => [s.id, s.name]));
    return (id) => map.get(id) || "—";
  }, [stores]);
  const deptName = useMemo(() => {
    const map = new Map(departments.map((d) => [d.id, d.name]));
    return (id) => map.get(id) || "—";
  }, [departments]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return requests;
    return requests.filter((r) => {
      const hay =
        (r.name_guess || "") +
        " " +
        (r.barcode || "") +
        " " +
        (r.notes || "");
      return hay.toLowerCase().includes(term);
    });
  }, [requests, q]);

  const GRID = "minmax(0,2fr) 1fr 1fr 1fr 1fr 1fr 1fr";

  return (
    <div data-testid="operator-product-requests-page">
      <div className="mb-4">
        <Link
          to={`/platform/companies/${cid}`}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-ink"
          data-testid="operator-product-requests-back"
        >
          <ArrowLeft size={14} />
          Back to tenant
        </Link>
      </div>

      <PageHeader
        eyebrow={`Tenant · ${companyName || cid}`}
        title={
          status === "all"
            ? "Product requests"
            : `Product requests · ${status}`
        }
        subtitle={`${filtered.length} request${filtered.length === 1 ? "" : "s"} matching filters. Photo previews are stripped from this list view.`}
      />

      <Card variant="raised" className="p-3 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              value={q}
              onChange={(e) => setFilter("q", e.target.value)}
              placeholder="Name, barcode, notes…"
              className="pl-9"
              data-testid="operator-product-requests-search"
            />
          </div>
          <Select
            value={status}
            onChange={(e) => setFilter("status", e.target.value)}
            data-testid="operator-product-requests-filter-status"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="all">All statuses</option>
          </Select>
          <Select
            value={storeId}
            onChange={(e) => setFilter("store", e.target.value)}
            data-testid="operator-product-requests-filter-store"
          >
            <option value="">All stores</option>
            {stores.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
          <Select
            value={deptId}
            onChange={(e) => setFilter("department", e.target.value)}
            data-testid="operator-product-requests-filter-department"
          >
            <option value="">All departments</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
        </div>
        {(q || storeId || deptId || status !== "pending") && (
          <div className="mt-3 flex justify-end">
            <Button
              tier="tertiary"
              size="sm"
              onClick={() => setParams({}, { replace: true })}
              data-testid="operator-product-requests-clear-filters"
            >
              Reset filters
            </Button>
          </div>
        )}
      </Card>

      <OperatorTable
        gridTemplate={GRID}
        header={
          <>
            <div>Product</div>
            <div>Barcode</div>
            <div>Store</div>
            <div>Department</div>
            <div>Submitted by</div>
            <div>Status</div>
            <div>Created</div>
          </>
        }
        loading={loading}
        empty={!loading && filtered.length === 0}
        emptyState={
          <div className="p-10">
            <EmptyState
              icon={<Inbox size={32} />}
              title="No product requests match"
              description="Try changing the status filter or clearing filters."
              data-testid="operator-product-requests-empty"
            />
          </div>
        }
        data-testid="operator-product-requests-table"
      >
        {filtered.map((r) => (
          <OperatorTableRow
            key={r.id}
            gridTemplate={GRID}
            data-testid={`operator-product-request-row-${r.id}`}
          >
            <div className="min-w-0">
              <div className="font-display font-semibold truncate">
                {r.name_guess || "(no name guess)"}
              </div>
              {r.notes && (
                <div className="font-mono text-[11px] text-muted-foreground truncate">
                  {r.notes}
                </div>
              )}
            </div>
            <div className="font-mono text-xs truncate">
              {r.barcode || "—"}
            </div>
            <div className="font-mono text-xs truncate">
              {storeName(r.store_id)}
            </div>
            <div className="font-mono text-xs truncate">
              {deptName(r.department_id)}
            </div>
            <div className="font-mono text-[11px] truncate">
              {r.submitted_by_email || r.submitted_by_name || "—"}
            </div>
            <div>
              <StatusChip tone={STATUS_TONE[r.status] || "neutral"}>
                {r.status || "—"}
              </StatusChip>
            </div>
            <div className="font-mono text-[11px] text-muted-foreground">
              {(r.created_at || "").slice(0, 10) || "—"}
            </div>
          </OperatorTableRow>
        ))}
      </OperatorTable>
    </div>
  );
}
